import React, { useState, useContext, useEffect, useCallback } from 'react';
import { translate } from 'react-multi-lang';
import { DownloadOutlined, UploadOutlined, CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';

import { Row, Col, Button, Upload, Table, Progress, Space, Divider, Alert, message, Tag, Spin } from 'antd';
import moment from 'moment';
import 'moment/locale/ca';
import 'moment/locale/es';

import * as XLSX from 'xlsx';

import { Api } from '../../../../helpers';
//import { PerfilUsuariContext } from "../../../../context/perfil-usuari";

// ---------- Constants ----------
// Columnes que s'envien a la BD (taula alumnes_classes)
const COLUMNES_CLAU = [
  'id',
  'id_alumne',
  'id_classe',
  'id_curs',
  'id_curs_escolar'
];

// Columnes informatives (es mostren al preview però no s'envien)
const COLUMNES_INFO = [
  'cognom1',
  'cognom2',
  'nom',
  'Classe',
  'Curs',
  'CursEscolar'
];

// Totes les columnes del full (ordre del fitxer original)
const COLUMNES_FULL = [
  'id',
  'id_alumne',
  'cognom1',
  'cognom2',
  'nom',
  'id_classe',
  'Classe',
  'id_curs',
  'Curs',
  'id_curs_escolar',
  'CursEscolar'
];

// ---------- Helpers ----------
const esEnter = (v) => v !== null && v !== undefined && v !== '' && Number.isInteger(Number(v)) && !isNaN(Number(v));

function normalitzarFila(fila){
  // Passa totes les claus a lowercase + trim
  const nova = {};
  Object.keys(fila).forEach((k) => {
    nova[String(k).trim().toLowerCase()] = fila[k];
  });
  return nova;
}

function validarFila(fila, mapClasses, cursEscolarTriat){
  const errors = [];
  const avisos = [];

  // Presència de les columnes clau
  COLUMNES_CLAU.forEach((c) => {
    if(fila[c] === undefined || fila[c] === null || fila[c] === ''){
      errors.push(`Falta '${c}'`);
    }
  });
  if(errors.length > 0) return { errors, avisos };

  // id: enter >= 0
  if(!esEnter(fila.id) || Number(fila.id) < 0){
    errors.push("id ha de ser un enter >= 0 (0 = insert, >0 = update)");
  }

  // id_alumne: enter > 0
  if(!esEnter(fila.id_alumne) || Number(fila.id_alumne) <= 0){
    errors.push("id_alumne ha de ser un enter > 0");
  }

  // id_curs_escolar: enter > 0
  if(!esEnter(fila.id_curs_escolar) || Number(fila.id_curs_escolar) <= 0){
    errors.push("id_curs_escolar ha de ser un enter > 0");
  }

  // id_classe i id_curs: enter > 0
  if(!esEnter(fila.id_classe) || Number(fila.id_classe) <= 0){
    errors.push("id_classe ha de ser un enter > 0");
  }
  if(!esEnter(fila.id_curs) || Number(fila.id_curs) <= 0){
    errors.push("id_curs ha de ser un enter > 0");
  }

  // Coherència id_classe <-> id_curs (requerim mapa carregat)
  if(errors.length === 0){
    const idCursEscolar = Number(fila.id_curs_escolar);
    const idClasse = Number(fila.id_classe);
    const idCursExcel = Number(fila.id_curs);

    const mapaCurs = mapClasses[idCursEscolar];
    if(!mapaCurs){
      errors.push(`No s'ha pogut carregar el mapa de classes per id_curs_escolar=${idCursEscolar}`);
    }else if(!(String(idClasse) in mapaCurs) && !(idClasse in mapaCurs)){
      errors.push(`id_classe=${idClasse} no existeix al curs escolar ${idCursEscolar}`);
    }else{
      const idCursEsperat = mapaCurs[idClasse] !== undefined ? mapaCurs[idClasse] : mapaCurs[String(idClasse)];
      if(Number(idCursEsperat) !== idCursExcel){
        errors.push(`id_curs=${idCursExcel} no coherent amb id_classe=${idClasse} (esperat ${idCursEsperat})`);
      }
    }

    // Avís si id_curs_escolar difereix del perfil
    if(idCursEscolar !== Number(cursEscolarTriat)){
      avisos.push(`id_curs_escolar=${idCursEscolar} difereix del curs actual del perfil (${cursEscolarTriat})`);
    }
  }

  return { errors, avisos };
}

// ---------- Component ----------
function AlumnesClassesImportar(props){

  //const [perfilUsuari] = useContext(PerfilUsuariContext);
  //const cursEscolarTriat = perfilUsuari.curs_escolar_actual.id;
  const cursEscolarTriat = props.cursEscolarTriat;

  const [files, setFiles] = useState([]);              // files parsejades + validació
  const [nomFitxer, setNomFitxer] = useState(null);
  const [importantEstat, setImportantEstat] = useState('idle'); // idle|processant|acabat
  const [progres, setProgres] = useState(0);
  const [resultats, setResultats] = useState([]);      // per fila: {fila, ok, accio, id_retorn, missatge}

  // mapClasses: { [id_curs_escolar]: { [id_classe]: id_curs } }
  const [mapClasses, setMapClasses] = useState({});
  const [carregantClasses, setCarregantClasses] = useState(false);

  // ---------- Càrrega de classes per id_curs_escolar ----------
  const carregarClasses = useCallback(async (idCursEscolar) => {
    if(!idCursEscolar) return null;
    try{
      const resposta = await Api.get('/classes/all/' + idCursEscolar);
      if(resposta && resposta.result && resposta.result.success === 1 && resposta.data && resposta.data.classes){
        const mapa = {};
        resposta.data.classes.forEach((c) => {
          mapa[Number(c.id)] = c.id_curs === null ? null : Number(c.id_curs);
        });
        setMapClasses((prev) => ({ ...prev, [Number(idCursEscolar)]: mapa }));
        return mapa;
      }
      return null;
    }catch(err){
      console.error('Error carregant classes per id_curs_escolar=' + idCursEscolar, err);
      return null;
    }
  }, []);

  // Carregar el curs actual al muntar
  useEffect(() => {
    carregarClasses(cursEscolarTriat);
  }, [cursEscolarTriat, carregarClasses]);

  // ---------- Descarrega de la plantilla ----------
  const descarregarPlantilla = () => {
    const capceleres = COLUMNES_FULL;
    const exemple = [
      0,                // id            (0 = insert)
      100,              // id_alumne
      'Domenech',       // cognom1
      'Perez',          // cognom2
      'Ander',          // nom
      174,              // id_classe
      'Nursery_A',      // Classe
      13,               // id_curs
      'Nursery',        // Curs
      7,                // id_curs_escolar
      '2026-27'         // CursEscolar
    ];
    const wsAlumnes = XLSX.utils.aoa_to_sheet([capceleres, exemple]);

    const instruccions = [
      ['Instruccions per omplir la plantilla'],
      [''],
      ['1. Cada fila del full "AlumnesClasses" representa una relació alumne-classe per a un curs escolar.'],
      ['2. No canviïs els noms de les columnes.'],
      ['3. Columnes clau obligatòries: id, id_alumne, id_classe, id_curs, id_curs_escolar.'],
      ['4. Les columnes cognom1, cognom2, nom, Classe, Curs, CursEscolar són informatives (no s\'importen).'],
      [''],
      ['Regla insert/update:'],
      ['id = 0          -> INSERT (es crea una nova relació)'],
      ['id > 0          -> UPDATE (s\'actualitza la relació existent)'],
      [''],
      ['Coherències:'],
      ['id_classe      -> ha d\'existir dins el id_curs_escolar indicat'],
      ['id_curs        -> ha de coincidir amb el id_curs de la classe (error bloquejant si no coincideix)'],
      ['id_curs_escolar-> enter > 0. Avís si difereix del curs actual del perfil.'],
      [''],
      ['Exemple:'],
      ['id=0, id_alumne=100, id_classe=174, id_curs=13, id_curs_escolar=7 -> INSERT'],
      ['id=5, id_alumne=101, id_classe=175, id_curs=13, id_curs_escolar=7 -> UPDATE de la relació #5'],
    ];
    const wsInstr = XLSX.utils.aoa_to_sheet(instruccions);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsAlumnes, 'AlumnesClasses');
    XLSX.utils.book_append_sheet(wb, wsInstr, 'Instruccions');
    XLSX.writeFile(wb, 'plantilla_alumnes_classes.xlsx');
  };

  // ---------- Lectura del fitxer ----------
  const llegirFitxer = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try{
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sheetName = wb.SheetNames.includes('AlumnesClasses') ? 'AlumnesClasses' : wb.SheetNames[0];
        const sheet = wb.Sheets[sheetName];
        const filesRaw = XLSX.utils.sheet_to_json(sheet, { defval: null });

        // Normalitzar totes les files
        const normFiles = filesRaw.map((fila) => normalitzarFila(fila));

        // Recollir id_curs_escolar únics que encara no estiguin al cache
        const idsCursEscolarUnics = Array.from(new Set(
          normFiles
            .map((f) => Number(f.id_curs_escolar))
            .filter((n) => !isNaN(n) && n > 0)
        ));
        const pendents = idsCursEscolarUnics.filter((n) => !(n in mapClasses));

        let nouMap = { ...mapClasses };
        if(pendents.length > 0){
          setCarregantClasses(true);
          try{
            const resultats = await Promise.all(pendents.map((id) => carregarClasses(id)));
            // carregarClasses ja fa setMapClasses per cada un; reconstruim a partir de l'estat actual
            // Però per tenir el mapa disponible tot seguit per validar, el construim manualment:
            pendents.forEach((id, i) => {
              const r = resultats[i];
              if(r) nouMap[id] = r;
            });
          }finally{
            setCarregantClasses(false);
          }
        }

        // Validar files amb el mapa (necessari perque carregarClasses pot haver fet setMapClasses asíncronament)
        const validades = normFiles.map((fila, idx) => {
          const { errors, avisos } = validarFila(fila, nouMap, cursEscolarTriat);
          return {
            key: idx,
            fila: idx + 2, // +2: fila 1 = capçalera
            dades: fila,
            errors,
            avisos
          };
        });

        setFiles(validades);
        setNomFitxer(file.name);
        setResultats([]);
        setImportantEstat('idle');
        setProgres(0);

        if(validades.length === 0){
          message.warning('El fitxer no conté cap fila de dades.');
        }
      }catch(err){
        console.error('Error llegint Excel:', err);
        message.error('No s\'ha pogut llegir el fitxer Excel.');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // evitar upload automàtic d'antd
  };

  // ---------- Importació ----------
  const importar = async () => {
    const filesValides = files.filter(f => f.errors.length === 0);
    if(filesValides.length === 0){
      message.warning('No hi ha cap fila vàlida per importar.');
      return;
    }

    setImportantEstat('processant');
    setProgres(0);
    const resultatsAcumulats = [];

    for(let i = 0; i < filesValides.length; i++){
      const item = filesValides[i];
      const d = item.dades;

      const idOriginal = Number(d.id);
      const accio = idOriginal === 0 ? 'insert' : 'update';

      const body = {
        id_alumne: Number(d.id_alumne),
        id_classe: Number(d.id_classe),
        id_curs: Number(d.id_curs),
        id_curs_escolar: Number(d.id_curs_escolar)
      };

      let resFila = {
        key: item.key,
        fila: item.fila,
        ok: false,
        accio,
        id_original: idOriginal,
        id_retorn: null,
        missatge: ''
      };

      try{
        let resposta;
        if(accio === 'insert'){
          resposta = await Api.myFetch('/cursescolar/alumnes_classes', 'POST', JSON.stringify(body));
          if(resposta && resposta.result && resposta.result.success === 1){
            const idNou = resposta.data && resposta.data.id;
            resFila.ok = true;
            resFila.id_retorn = idNou;
            resFila.missatge = `Relació #${idNou} creada (alumne ${body.id_alumne} → classe ${body.id_classe})`;
          }else{
            resFila.ok = false;
            resFila.missatge = `Error insert: ${(resposta && resposta.data && resposta.data.message) || 'error desconegut'}`;
          }
        }else{
          resposta = await Api.myFetch('/cursescolar/alumnes_classes/' + idOriginal, 'PUT', JSON.stringify(body));
          if(resposta && resposta.result && resposta.result.success === 1){
            resFila.ok = true;
            resFila.id_retorn = idOriginal;
            resFila.missatge = `Relació #${idOriginal} actualitzada (alumne ${body.id_alumne} → classe ${body.id_classe})`;
          }else{
            resFila.ok = false;
            resFila.missatge = `Error update #${idOriginal}: ${(resposta && resposta.data && resposta.data.message) || 'error desconegut'}`;
          }
        }
      }catch(err){
        resFila.ok = false;
        resFila.missatge = `Excepció (${accio}): ${err.message || err}`;
      }

      resultatsAcumulats.push(resFila);
      setResultats([...resultatsAcumulats]);
      setProgres(Math.round(((i + 1) / filesValides.length) * 100));
    }

    setImportantEstat('acabat');
    const ok = resultatsAcumulats.filter(r => r.ok).length;
    const ko = resultatsAcumulats.length - ok;
    if(ko === 0){
      message.success(`${ok} relacions importades correctament.`, 6);
    }else{
      message.warning(`Importació finalitzada: ${ok} OK, ${ko} amb error.`, 8);
    }
  };

  // ---------- Columnes de les taules ----------
  const nErrors = files.filter(f => f.errors.length > 0).length;
  const nAvisos = files.filter(f => f.errors.length === 0 && f.avisos.length > 0).length;
  const nValides = files.filter(f => f.errors.length === 0).length;

  const colsPreview = [
    { title: 'Fila', dataIndex: 'fila', key: 'fila', width: 60, fixed: 'left' },
    {
      title: 'Estat',
      key: 'estat',
      width: 90,
      fixed: 'left',
      render: (_, r) => {
        if(r.errors.length > 0) return <Tag color="red">Error</Tag>;
        if(r.avisos.length > 0) return <Tag color="orange">Avís</Tag>;
        return <Tag color="green">OK</Tag>;
      }
    },
    { title: 'id',             dataIndex: ['dades','id'],             key: 'id',             width: 70 },
    { title: 'id_alumne',      dataIndex: ['dades','id_alumne'],      key: 'id_alumne',      width: 100 },
    { title: 'cognom1',        dataIndex: ['dades','cognom1'],        key: 'cognom1' },
    { title: 'cognom2',        dataIndex: ['dades','cognom2'],        key: 'cognom2' },
    { title: 'nom',            dataIndex: ['dades','nom'],            key: 'nom' },
    { title: 'id_classe',      dataIndex: ['dades','id_classe'],      key: 'id_classe',      width: 100 },
    { title: 'Classe',         dataIndex: ['dades','classe'],         key: 'Classe' },
    { title: 'id_curs',        dataIndex: ['dades','id_curs'],        key: 'id_curs',        width: 90 },
    { title: 'Curs',           dataIndex: ['dades','curs'],           key: 'Curs' },
    { title: 'id_curs_escolar',dataIndex: ['dades','id_curs_escolar'],key: 'id_curs_escolar',width: 130 },
    { title: 'CursEscolar',    dataIndex: ['dades','cursescolar'],    key: 'CursEscolar' },
    {
      title: 'Missatges',
      key: 'msg',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          {r.errors.map((e, i) => <span key={'e'+i} style={{color:'#cf1322'}}>• {e}</span>)}
          {r.avisos.map((a, i) => <span key={'a'+i} style={{color:'#d46b08'}}>• {a}</span>)}
        </Space>
      )
    }
  ];

  const colsResultats = [
    { title: 'Fila', dataIndex: 'fila', key: 'fila', width: 60 },
    {
      title: 'Estat',
      key: 'estat',
      width: 70,
      render: (_, r) => r.ok
        ? <CheckCircleTwoTone twoToneColor="#52c41a" />
        : <CloseCircleTwoTone twoToneColor="#cf1322" />
    },
    {
      title: 'Acció',
      dataIndex: 'accio',
      key: 'accio',
      width: 90,
      render: (a) => a === 'insert'
        ? <Tag color="blue">Insert</Tag>
        : <Tag color="purple">Update</Tag>
    },
    { title: 'id original', dataIndex: 'id_original', key: 'id_original', width: 100 },
    { title: 'id retornat', dataIndex: 'id_retorn', key: 'id_retorn', width: 100 },
    { title: 'Missatge', dataIndex: 'missatge', key: 'missatge' }
  ];

  const podeImportar =
    files.length > 0 &&
    nValides > 0 &&
    importantEstat !== 'processant' &&
    !carregantClasses;

  return (
    <div style={{ padding: 20 }}>
      <Row gutter={16} align="middle">
        <Col>
          <Button icon={<DownloadOutlined />} onClick={descarregarPlantilla}>
            {props.t ? props.t('alumnes_classes.import.descarregar_plantilla') || 'Descarregar plantilla' : 'Descarregar plantilla'}
          </Button>
        </Col>
        <Col>
          <Upload
            accept=".xlsx,.xls"
            beforeUpload={llegirFitxer}
            showUploadList={false}
            disabled={importantEstat === 'processant' || carregantClasses}
          >
            <Button icon={<UploadOutlined />} disabled={carregantClasses}>
              {props.t ? props.t('alumnes_classes.import.triar_fitxer') || 'Triar fitxer Excel' : 'Triar fitxer Excel'}
            </Button>
          </Upload>
        </Col>
        {carregantClasses && (
          <Col>
            <Spin size="small" /> <span style={{ color: '#555' }}>Carregant catàleg de classes…</span>
          </Col>
        )}
        {nomFitxer && !carregantClasses && (
          <Col>
            <span style={{ color: '#555' }}>{nomFitxer}</span>
          </Col>
        )}
      </Row>

      {files.length > 0 && (
        <>
          <Divider />
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col>
              <Alert
                type={nErrors > 0 ? 'error' : (nAvisos > 0 ? 'warning' : 'success')}
                showIcon
                message={
                  <>
                    Total files: <b>{files.length}</b> · Vàlides: <b>{nValides}</b> · Amb avís: <b>{nAvisos}</b> · Amb error: <b>{nErrors}</b>
                  </>
                }
              />
            </Col>
          </Row>

          <Table
            size="small"
            columns={colsPreview}
            dataSource={files}
            pagination={{ pageSize: 20, position: ['topRight', 'bottomRight'] }}
            scroll={{ x: 1700 }}
            rowClassName={(r) => r.errors.length > 0 ? 'row-error' : (r.avisos.length > 0 ? 'row-warn' : '')}
          />

          <Row gutter={16} style={{ marginTop: 16 }} align="middle">
            <Col>
              <Button onClick={props.callback}>
                {props.t ? props.t('generic.back') : 'Enrere'}
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                onClick={importar}
                loading={importantEstat === 'processant'}
                disabled={!podeImportar}
              >
                Importar {nValides} relació(ns)
              </Button>
            </Col>
            {importantEstat !== 'idle' && (
              <Col flex="auto">
                <Progress percent={progres} status={importantEstat === 'acabat' ? 'success' : 'active'} />
              </Col>
            )}
          </Row>
        </>
      )}

      {resultats.length > 0 && (
        <>
          <Divider>Resultats de la importació</Divider>
          <Table
            size="small"
            columns={colsResultats}
            dataSource={resultats}
            pagination={{ pageSize: 20, position: ['topRight', 'bottomRight'] }}
          />
        </>
      )}

      {files.length === 0 && !carregantClasses && (
        <Row style={{ marginTop: 20 }}>
          <Col>
            <Button onClick={props.callback}>
              {props.t ? props.t('generic.back') : 'Enrere'}
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default translate(AlumnesClassesImportar);
